import React, { createContext, useContext, useState, useCallback } from 'react';

const SelectionContext = createContext(null);

export const useSelection = () => {
  const context = useContext(SelectionContext);
  if (!context) {
    throw new Error('useSelection must be used within a SelectionProvider');
  }
  return context;
};

export const SelectionProvider = ({ children }) => {
  // View 1 selections (left or single view)
  const [view1Origin, setView1Origin] = useState(null);
  const [view1Destination, setView1Destination] = useState(null);
  const [view1DayType, setView1DayType] = useState(null);
  const [view1TimeSlot, setView1TimeSlot] = useState(null);

  // View 2 selections (right view in compare mode)
  const [view2Origin, setView2Origin] = useState(null);
  const [view2Destination, setView2Destination] = useState(null);
  const [view2DayType, setView2DayType] = useState(null);
  const [view2TimeSlot, setView2TimeSlot] = useState(null);

  // Clear selections for a specific view
  const clearSelection = useCallback((viewId = 1) => {
    if (viewId === 2) {
      setView2Origin(null);
      setView2Destination(null);
    } else {
      setView1Origin(null);
      setView1Destination(null);
    }
  }, []);

  // Swap origin and destination for a specific view
  const swapOriginDestination = useCallback((viewId = 1) => {
    if (viewId === 2) {
      setView2Origin((prevOrigin) => {
        setView2Destination(prevOrigin);
        return view2Destination;
      });
    } else {
      setView1Origin((prevOrigin) => {
        setView1Destination(prevOrigin);
        return view1Destination;
      });
    }
  }, [view1Destination, view2Destination]);

  // Get selection for a specific view
  const getViewSelection = useCallback(
    (viewId) => {
      if (viewId === 2) {
        return {
          origin: view2Origin,
          destination: view2Destination,
          dayType: view2DayType,
          timeSlot: view2TimeSlot,
          setOrigin: setView2Origin,
          setDestination: setView2Destination,
          setDayType: setView2DayType,
          setTimeSlot: setView2TimeSlot,
        };
      }
      return {
        origin: view1Origin,
        destination: view1Destination,
        dayType: view1DayType,
        timeSlot: view1TimeSlot,
        setOrigin: setView1Origin,
        setDestination: setView1Destination,
        setDayType: setView1DayType,
        setTimeSlot: setView1TimeSlot,
      };
    },
    [view1Origin, view1Destination, view1DayType, view1TimeSlot, view2Origin, view2Destination, view2DayType, view2TimeSlot]
  );

  // Check if a zone is selected as origin or destination for a specific view
  const isZoneSelected = useCallback(
    (zoneName, viewId = 1) => {
      if (viewId === 2) {
        return zoneName === view2Origin || zoneName === view2Destination;
      }
      return zoneName === view1Origin || zoneName === view1Destination;
    },
    [view1Origin, view1Destination, view2Origin, view2Destination]
  );

  // Get the role of a zone (origin, destination, or null) for a specific view
  const getZoneRole = useCallback(
    (zoneName, viewId = 1) => {
      if (viewId === 2) {
        if (zoneName === view2Origin) return 'origin';
        if (zoneName === view2Destination) return 'destination';
        return null;
      }
      if (zoneName === view1Origin) return 'origin';
      if (zoneName === view1Destination) return 'destination';
      return null;
    },
    [view1Origin, view1Destination, view2Origin, view2Destination]
  );

  // Legacy accessors for backward compatibility (use view 1)
  const origin = view1Origin;
  const destination = view1Destination;
  const setOrigin = setView1Origin;
  const setDestination = setView1Destination;

  const value = {
    // Legacy Origin/Destination (view 1)
    origin,
    destination,
    setOrigin,
    setDestination,
    clearSelection,
    swapOriginDestination,
    isZoneSelected,
    getZoneRole,

    // View 1 specific
    view1Origin,
    view1Destination,
    view1DayType,
    view1TimeSlot,
    setView1Origin,
    setView1Destination,
    setView1DayType,
    setView1TimeSlot,

    // View 2 specific
    view2Origin,
    view2Destination,
    view2DayType,
    view2TimeSlot,
    setView2Origin,
    setView2Destination,
    setView2DayType,
    setView2TimeSlot,

    // Unified getter
    getViewSelection,
  };

  return (
    <SelectionContext.Provider value={value}>
      {children}
    </SelectionContext.Provider>
  );
};

export default SelectionContext;
